import { useSignal } from "@preact/signals";

export type SparklineTone = "cpu" | "ram" | "disk" | "rcon";
export const SPARKLINE_COLLECTING_LABEL = "Collecting telemetry…";

/** Prozent-Metriken (CPU/RAM/Disk) vs. reine Zähler (RCON QPM). */
export type SparklineValueKind = "percent" | "count";

export interface SparklineAxisLabels {
  xStart?: string;
  xEnd?: string;
  yTop?: string;
  yBottom?: string;
}

export function compactSparklineValues(values: Array<number | null | undefined>, maxPoints = 60): number[] {
  return values
    .filter((value): value is number => Number.isFinite(value))
    .slice(-Math.max(1, maxPoints));
}

export function sparklinePolyline(
  values: Array<number | null | undefined>,
  width = 120,
  height = 32,
  scaleMax?: number,
  paddingRatio = 0.16,
): string {
  const data = compactSparklineValues(values);
  if (data.length < 2) return "";
  // Feste Domain [0, scaleMax] für Prozent-Metriken (CPU/RAM/Disk) → kleine
  // Änderungen (z.B. 11→12 %) bleiben kleine Ausschläge, kein Auto-Zoom. Ohne
  // scaleMax (z.B. RCON QPM, Counter) bleibt es bei dynamischem lokalem min/max.
  const fixed = Number.isFinite(scaleMax) && (scaleMax as number) > 0;
  const min = fixed ? 0 : Math.min(...data);
  const max = fixed ? (scaleMax as number) : Math.max(...data);
  const range = max - min;
  const topPad = height * Math.max(0, Math.min(0.45, paddingRatio));
  const bottomPad = topPad;
  const plotHeight = Math.max(1, height - topPad - bottomPad);
  const step = width / (data.length - 1);
  return data
    .map((value, index) => {
      const ratio = range <= 0.000001 ? 0.5 : Math.max(0, Math.min(1, (value - min) / range));
      const x = Math.round(index * step * 100) / 100;
      const y = Math.round((height - bottomPad - ratio * plotHeight) * 100) / 100;
      return `${x},${y}`;
    })
    .join(" ");
}

export function sparklineIsFlat(values: Array<number | null | undefined>, epsilon = 0.000001): boolean {
  const data = compactSparklineValues(values);
  if (data.length < 2) return false;
  return Math.max(...data) - Math.min(...data) <= epsilon;
}

export function sparklineAreaPath(points: string, width = 120, height = 32): string {
  if (!points) return "";
  const first = points.split(" ")[0];
  const last = points.split(" ").slice(-1)[0];
  if (!first || !last) return "";
  const firstX = first.split(",")[0];
  const lastX = last.split(",")[0];
  return `M${firstX},${height} L${points} L${lastX},${height} Z`;
}

export function sparklineAxisLabels(tone: SparklineTone): Required<SparklineAxisLabels> {
  // CPU = Load als %-Auslastung der Cores (load1/cores·100, fixe Skala 0–100 %).
  if (tone === "rcon") return { xStart: "-60s", xEnd: "now", yTop: "peak", yBottom: "0" };
  return { xStart: "-60s", xEnd: "now", yTop: "100%", yBottom: "0%" };
}

/** Wertart je Tone: CPU/RAM/Disk = Prozent, RCON = Zähler. */
export function sparklineValueKind(tone: SparklineTone): SparklineValueKind {
  return tone === "rcon" ? "count" : "percent";
}

/**
 * Index des Datenpunkts, der am nächsten an `x` (Pixel innerhalb der Plotbreite
 * `width`) liegt. Robust: `count<=0` → -1 (kein Punkt), `count===1` → 0,
 * ungültige `x`/`width` → 0; sonst auf `[0, count-1]` begrenzt.
 */
export function nearestSparklineIndex(x: number, width: number, count: number): number {
  if (!Number.isFinite(count) || count <= 0) return -1;
  if (count === 1 || !Number.isFinite(width) || width <= 0) return 0;
  const xf = Number.isFinite(x) ? x : 0;
  const frac = Math.max(0, Math.min(1, xf / width));
  return Math.max(0, Math.min(count - 1, Math.round(frac * (count - 1))));
}

/** Tooltip-Wert: Prozent → `12%`, Zähler → `600`. NaN/Infinity → `—`. */
export function formatSparklineValue(value: number, kind: SparklineValueKind): string {
  if (!Number.isFinite(value)) return "—";
  return kind === "percent" ? `${Math.round(value)}%` : `${Math.round(value)}`;
}

/** Zeit-Offset eines Punkts relativ zum letzten ("now"): `-25s` bzw. `now`. */
export function sparklineOffsetLabel(index: number, count: number, secondsPerPoint: number): string {
  if (!Number.isFinite(index) || !Number.isFinite(count) || count <= 0) return "";
  const back = (count - 1 - index) * Math.max(0, secondsPerPoint);
  return back <= 0 ? "now" : `-${Math.round(back)}s`;
}

const PLOT_X = 18;
const PLOT_Y = 4;
const PLOT_W = 98;
const PLOT_H = 22;
const VB_W = 120;
const VB_H = 32;

export function MetricSparkline({
  values,
  tone,
  label,
  scaleMax,
  axis,
  hoverIndex = -1,
  onHover,
  onLeave,
  secondsPerPoint = 5,
}: {
  values: Array<number | null | undefined>;
  tone: SparklineTone;
  label: string;
  scaleMax?: number;
  axis?: SparklineAxisLabels;
  /** Aktuell gehoverter Punkt-Index (in der kompaktierten Serie); -1 = keiner. */
  hoverIndex?: number;
  onHover?: (index: number) => void;
  onLeave?: () => void;
  secondsPerPoint?: number;
}) {
  const labels = { ...sparklineAxisLabels(tone), ...(axis ?? {}) };
  const data = compactSparklineValues(values);
  const points = sparklinePolyline(values, PLOT_W, PLOT_H, scaleMax);
  const area = sparklineAreaPath(points, PLOT_W, PLOT_H);
  const hasLine = Boolean(points);
  const flat = sparklineIsFlat(values);

  const tokens = hasLine ? points.split(" ") : [];
  const active = hasLine && hoverIndex >= 0 && hoverIndex < tokens.length;
  const marker = active ? tokens[hoverIndex]!.split(",").map(Number) : null;
  const kind = sparklineValueKind(tone);

  // Pointer → nearest index (nur wenn ein Hover-Callback gesetzt ist).
  function handleMove(e: { currentTarget: HTMLElement; clientX: number }) {
    if (!onHover || !hasLine) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    const xPx = e.clientX - rect.left - (PLOT_X / VB_W) * rect.width;
    const plotPx = (PLOT_W / VB_W) * rect.width;
    onHover(nearestSparklineIndex(xPx, plotPx, data.length));
  }

  // Tooltip-Positionierung in %-Container-Koordinaten (SVG ist linear gestreckt).
  let tip: { left: string; top: string; transform: string; value: string; offset: string } | null = null;
  if (marker) {
    const vbX = PLOT_X + marker[0]!;
    const vbY = PLOT_Y + marker[1]!;
    const leftPct = (vbX / VB_W) * 100;
    const topPct = (vbY / VB_H) * 100;
    const tx = leftPct < 18 ? "0%" : leftPct > 82 ? "-100%" : "-50%";
    const ty = topPct < 38 ? "8px" : "calc(-100% - 8px)";
    tip = {
      left: `${leftPct}%`,
      top: `${topPct}%`,
      transform: `translate(${tx}, ${ty})`,
      value: formatSparklineValue(data[hoverIndex]!, kind),
      offset: sparklineOffsetLabel(hoverIndex, data.length, secondsPerPoint),
    };
  }

  return (
    <div
      class={`metric-sparkline ${tone} ${hasLine ? "" : "collecting"} ${flat ? "flat" : ""}`}
      data-sparkline={tone}
      aria-label={label}
      onPointerMove={onHover ? handleMove : undefined}
      onPointerLeave={onLeave ? () => onLeave() : undefined}
    >
      <svg viewBox="0 0 120 32" preserveAspectRatio="none" role="img">
        <title>{label}</title>
        <path class="spark-grid" d={`M${PLOT_X} ${PLOT_Y + PLOT_H * 0.75}H${PLOT_X + PLOT_W} M${PLOT_X} ${PLOT_Y + PLOT_H * 0.5}H${PLOT_X + PLOT_W} M${PLOT_X} ${PLOT_Y + PLOT_H * 0.25}H${PLOT_X + PLOT_W}`} />
        <path class="spark-axis" d={`M${PLOT_X} ${PLOT_Y}V${PLOT_Y + PLOT_H}H${PLOT_X + PLOT_W}`} />
        <text class="spark-axis-label y top" x="2" y={PLOT_Y + 4}>{labels.yTop}</text>
        <text class="spark-axis-label y bottom" x="2" y={PLOT_Y + PLOT_H}>{labels.yBottom}</text>
        <text class="spark-axis-label x start" x={PLOT_X} y="31">{labels.xStart}</text>
        <text class="spark-axis-label x end" x="118" y="31" text-anchor="end">{labels.xEnd}</text>
        <g transform={`translate(${PLOT_X} ${PLOT_Y})`}>
          {hasLine ? (
            <>
              {!flat && <path class="spark-area" d={area} />}
              <polyline class="spark-line" points={points} />
              {marker && <circle class="spark-marker" cx={marker[0]} cy={marker[1]} r="2.1" />}
            </>
          ) : (
            <>
              <line class="spark-empty" x1="0" y1={PLOT_H * 0.68} x2={PLOT_W} y2={PLOT_H * 0.68} />
              <text class="spark-collecting" x="3" y="10">{SPARKLINE_COLLECTING_LABEL}</text>
            </>
          )}
        </g>
      </svg>
      {tip && (
        <div class="spark-tip" style={`left:${tip.left};top:${tip.top};transform:${tip.transform}`} aria-hidden="true">
          <span class="spark-tip-val">{tip.value}</span>
          {tip.offset && <span class="spark-tip-off">{tip.offset}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * Stateful Wrapper um `MetricSparkline`: hält den Hover-Index und reicht ihn
 * zusammen mit den Pointer-Callbacks durch. `MetricSparkline` selbst bleibt
 * hook-frei/rein (direkt in Tests aufrufbar).
 */
export function InteractiveSparkline(props: {
  values: Array<number | null | undefined>;
  tone: SparklineTone;
  label: string;
  scaleMax?: number;
  axis?: SparklineAxisLabels;
  secondsPerPoint?: number;
}) {
  const hover = useSignal(-1);
  return (
    <MetricSparkline
      {...props}
      hoverIndex={hover.value}
      onHover={(i) => (hover.value = i)}
      onLeave={() => (hover.value = -1)}
    />
  );
}
